import { type ReactNode } from 'react'

import { CopilotKit } from '@copilotkit/react-core'

import { ClarifyingQuestionProvider } from 'modules/ai/chat/providers/ClarifyingQuestionProvider'
import { FactorialChatRuntimeProvider } from 'modules/ai/chat/providers/FactorialChatRuntimeProvider'
import { OrderedMessagePartsProvider } from 'modules/ai/chat/providers/OrderedMessagePartsProvider'

type FactorialAiChatProviderProps = {
  children: ReactNode
  /** Mastra runtime endpoint (already includes `/copilotkit`). */
  runtimeUrl: string
  /** Default agent name CopilotKit boots with. */
  agent: string
  /** Authenticated fetch used for chat-history endpoints. */
  runtimeFetch: typeof fetch
  /** Optional gate run before each user submission. Return false to abort. */
  onBeforeSendMessage?: () => boolean | Promise<boolean>
}

// Hierarchy matters:
//   CopilotKit                  ← gives us the chat agent + actions
//     └ OrderedMessageParts     ← subscribes to agent events (needs agent)
//       └ FactorialChatRuntime  ← reads CopilotKit chat state + history
//         └ ClarifyingQuestion  ← composer-level UI state
// Reordering any pair silently degrades to no-op fallbacks.
export const FactorialAiChatProvider = ({
  children,
  runtimeUrl,
  agent,
  runtimeFetch,
  onBeforeSendMessage,
}: FactorialAiChatProviderProps) => (
  <CopilotKit runtimeUrl={runtimeUrl} agent={agent} credentials="include" showDevConsole={false}>
    <OrderedMessagePartsProvider>
      <FactorialChatRuntimeProvider
        runtimeFetch={runtimeFetch}
        onBeforeSendMessage={onBeforeSendMessage}
      >
        <ClarifyingQuestionProvider>{children}</ClarifyingQuestionProvider>
      </FactorialChatRuntimeProvider>
    </OrderedMessagePartsProvider>
  </CopilotKit>
)
